import { createSlice } from '@reduxjs/toolkit';
import { createEntityAdapter } from '@reduxjs/toolkit';
import { loadFormsByType } from './store/thunks/loadFormsByType';
import { loadFormsSummary } from './store/thunks/loadFormsSummary';
import { loadProject } from './store/thunks/loadProject';
import { loadResourcesWithEmail } from './store/thunks/loadResourcesWithEmail';
import { loadResourcesByUser } from './store/thunks/loadResourcesByUser';
import { loadFormSendData } from './store/thunks/loadFormSendData';
import { createForm } from './store/thunks/createForm';
import { deleteForm } from './store/thunks/deleteForm';
import { editForm } from './store/thunks/editForm';
import { sendForm } from './store/thunks/sendForm';
import { setNotes } from './store/thunks/setNotes';
import { getMasterParentProject } from './store/thunks/getMasterParentProject';
import { getParentSubProjects } from './store/thunks/getParentSubProjects';
import { getFormsByBusinessId } from './store/thunks/getFormsByBusinessId';
import * as projectFormActions from './store/actions/projectFormActions';
import { getNotes } from './store/thunks/getNotes';
import { setFormStateAsync } from './store/thunks/setFormStateAsync';
import { setFormNotesState } from './store/thunks/setFormNotesState';
import { createManualResource } from './store/thunks/createManualResource';
import { loadRecordsForResources } from './store/thunks/loadRecordsForResources';
import { createFormTemplate } from './store/thunks/createFormTemplate';
import { editFormTemplate } from './store/thunks/editFormTemplate';
import { loadFormTemplatesForProject } from './store/thunks/loadFormTemplatesForProject';
import { loadUsersForOrientation } from './store/thunks/loadUsersForOrientation';

import { createOrientationFormTemplate } from './store/thunks/createOrientationFormTemplate';
import { editOrientationFormTemplate } from './store/thunks/editOrientationFormTemplate';
import { loadDefaultFormTemplates } from './store/thunks/loadDefaultFormTemplates';
import { loadCurrentUserOrientationData } from './store/thunks/loadCurrentUserOrientationData.thunk';
import { downloadFormsByType } from './store/thunks/downloadFormsByType.thunk';
import { loadProjectFormVisibilityControl } from './store/thunks/loadProjectFormVisibilityControl';
import { saveProjectFormVisibilityControl } from './store/thunks/saveProjectFormVisibilityControl';

const formsAdapter = createEntityAdapter();
const projectAdapter = createEntityAdapter();
const subProjectsAdapter = createEntityAdapter();
const subProjectFormsAdapter = createEntityAdapter();
const masterParentAdapter = createEntityAdapter();
const resourcesAdapter = createEntityAdapter();
const formNotesAdapter = createEntityAdapter();
const manualResourcesAdapter = createEntityAdapter();
const recordsAdapter = createEntityAdapter();
const formTemplatesAdapter = createEntityAdapter();
const defaultTemplatesAdapter = createEntityAdapter();
const defaultWorkersState = {
  ownWorkers: [],
  subcontractorWorkers: [],
  subcontractorsByCompany: {},
};

const defaultFormsSummaryState = {
  groups: [],
  complete: true,
  status: 'idle',
  requestId: null,
};

// Whether a raw /forms/summary group row and a saved form describe the same
// bucket — mirrors the backend's own GROUP BY (type, originalType,
// template_group_id, template_id), used to keep formsSummary.groups counts
// truthful across local create/delete without a full re-fetch.
const formMatchesSummaryGroup = (group, form) =>
  group.type === form.type &&
  (group.originalType || null) === (form.originalType || null) &&
  (group.template_group_id || null) === (form.template_group_id || null) &&
  (group.template_id || null) === (form.template_id || null);

const resetState = {
  forms: createEntityAdapter().getInitialState(),
  project: createEntityAdapter().getInitialState(),
  subProjects: createEntityAdapter().getInitialState(),
  subProjectForms: createEntityAdapter().getInitialState(),
  masterParent: createEntityAdapter().getInitialState(),
  resources: createEntityAdapter().getInitialState(),
  formNotes: createEntityAdapter().getInitialState(),
  manualResources: createEntityAdapter().getInitialState(),
  records: createEntityAdapter().getInitialState(),
  formTemplates: createEntityAdapter().getInitialState(),
  defaultTemplates: createEntityAdapter().getInitialState(),
  projectFormVisibility: {
    projectConfig: null,
    effectiveConfig: {
      hiddenFormCollectionTypes: [],
      visibleTemplateGroupIds: [],
    },
    hasProjectOverride: false,
    source: 'defaultAll',
    sourceProjectId: null,
    templateCatalog: [],
  },
  projectFormVisibilityHydrated: false,
  loadingProjectFormVisibility: false,
  savingProjectFormVisibility: false,
  // Per form-type request status ('loading' | 'loaded' | 'error') written by
  // loadFormsByType, and the requestId each status belongs to (to drop stale
  // responses after a project switch or a resetFormTypeStatuses).
  formTypeStatus: {},
  formTypeRequestIds: {},
  // /forms/summary rollup for the lazy policy's folder headers.
  formsSummary: defaultFormsSummaryState,
  formState: {},
  workers: defaultWorkersState,
  orientationUsers: [],
  orientationUsersProjectId: null,
  loadingOrientationUsers: false,
  userOrientationData: {},
  loadingUserOrientationData: false,
  downloadUrl: null,
  downloadingFormsByType: false,
};

const projectFormSlice = createSlice({
  name: 'projectForm',
  initialState: {
    forms: formsAdapter.getInitialState(),
    project: projectAdapter.getInitialState(),
    subProjects: subProjectsAdapter.getInitialState(),
    subProjectForms: subProjectFormsAdapter.getInitialState(),
    masterParent: masterParentAdapter.getInitialState(),
    resources: resourcesAdapter.getInitialState(),
    formNotes: formNotesAdapter.getInitialState(),
    manualResources: manualResourcesAdapter.getInitialState(),
    records: recordsAdapter.getInitialState(),
    formTemplates: formTemplatesAdapter.getInitialState(),
    defaultTemplates: defaultTemplatesAdapter.getInitialState(),
    projectFormVisibility: {
      projectConfig: null,
      effectiveConfig: {
        hiddenFormCollectionTypes: [],
        visibleTemplateGroupIds: [],
      },
      hasProjectOverride: false,
      source: 'defaultAll',
      sourceProjectId: null,
      templateCatalog: [],
    },
    projectFormVisibilityHydrated: false,
    loadingProjectFormVisibility: false,
    savingProjectFormVisibility: false,
    formTypeStatus: {},
    formTypeRequestIds: {},
    formsSummary: defaultFormsSummaryState,
    formState: {
      loaded: false
    },
    workers: defaultWorkersState,
    orientationUsers: [],
    orientationUsersProjectId: null,
    loadingOrientationUsers: false,
    userOrientationData: {},
    loadingUserOrientationData: false,
    downloadUrl: null,
    downloadingFormsByType: false,
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadFormsByType.pending, (state, action) => {
        const { type } = action.meta.arg;
        state.formTypeStatus[type] = 'loading';
        state.formTypeRequestIds[type] = action.meta.requestId;
      })
      .addCase(loadFormsByType.fulfilled, (state, action) => {
        const { type, forms } = action.payload;
        // A project switch or a resetFormTypeStatuses in between means this
        // response is no longer the one anyone is waiting on — drop it.
        if (state.formTypeRequestIds[type] !== action.meta.requestId) {
          return;
        }
        formsAdapter.setMany(state.forms, forms);
        state.formTypeStatus[type] = 'loaded';
      })
      .addCase(loadFormsByType.rejected, (state, action) => {
        const { type } = action.meta.arg;
        if (state.formTypeRequestIds[type] !== action.meta.requestId) {
          return;
        }
        state.formTypeStatus[type] = 'error';
        state.error = action.error;
      })
      .addCase(loadFormsSummary.pending, (state, action) => {
        state.formsSummary.status = 'loading';
        state.formsSummary.requestId = action.meta.requestId;
      })
      .addCase(loadFormsSummary.fulfilled, (state, action) => {
        if (state.formsSummary.requestId !== action.meta.requestId) {
          return;
        }
        state.formsSummary.groups = action.payload.groups;
        state.formsSummary.complete = action.payload.complete;
        state.formsSummary.status = 'loaded';
      })
      .addCase(loadFormsSummary.rejected, (state, action) => {
        if (state.formsSummary.requestId !== action.meta.requestId) {
          return;
        }
        state.formsSummary.status = 'error';
        state.error = action.error;
      })
      .addCase(loadProject.pending, (state) => ({
        ...state,
        loadingProject: true,
      }))
      .addCase(loadProject.fulfilled, (state, action) => {
        state.loadingProject = false;
        projectAdapter.setMany(state.project, action.payload.project);
      })
      .addCase(loadProject.rejected, (state, action) => ({
        ...state,
        loadingProject: false,
        error: action.error,
      }))
      .addCase(loadUsersForOrientation.pending, (state, action) => {
        state.loadingOrientationUsers = true;
        // Opening another project must not leave the previous project's orientees
        // on screen while the new list loads. Reloads of the same project keep
        // theirs, so an open orientation dialog isn't torn down mid-fill.
        if (state.orientationUsersProjectId !== action.meta.arg) {
          state.orientationUsers = [];
        }
        state.orientationUsersProjectId = action.meta.arg;
      })
      .addCase(loadUsersForOrientation.fulfilled, (state, action) => {
        // Requests aren't cancelled on navigation: ignore a response for a project
        // we have already left, or it overwrites the current project's list.
        if (state.orientationUsersProjectId !== action.meta.arg) {
          return;
        }
        state.loadingOrientationUsers = false;
        // Always assign — an empty result means this project has no orientees,
        // not "keep whatever was here before".
        state.orientationUsers = action.payload.orientationUsers
          ? [...action.payload.orientationUsers]
          : [];
      })
      .addCase(loadUsersForOrientation.rejected, (state, action) => {
        if (state.orientationUsersProjectId !== action.meta.arg) {
          return;
        }
        state.loadingOrientationUsers = false;
        state.error = action.error;
      })
      .addCase(getFormsByBusinessId.pending, (state) => ({
        ...state,
        loading: true,
      }))
      .addCase(getFormsByBusinessId.fulfilled, (state, action) => {
        state.loading = false;
        subProjectFormsAdapter.setMany(
          state.subProjectForms,
          action.payload.subProjectForms
        );
      })
      .addCase(getFormsByBusinessId.rejected, (state, action) => ({
        ...state,
        loading: false,
        error: action.error,
      }))
      .addCase(loadCurrentUserOrientationData.pending, (state) => ({
        ...state,
        loadingUserOrientationData: true,
      }))
      .addCase(loadCurrentUserOrientationData.fulfilled, (state, action) => {
        state.loadingUserOrientationData = false;
        state.userOrientationData = action.payload.data;
      })
      .addCase(loadCurrentUserOrientationData.rejected, (state, action) => ({
        ...state,
        loadingUserOrientationData: false,
        error: action.error,
      }))

      .addCase(getMasterParentProject.pending, (state) => ({
        ...state,
        loading: true,
      }))
      .addCase(getMasterParentProject.fulfilled, (state, action) => {
        state.loading = false;
        masterParentAdapter.setMany(
          state.masterParent,
          action.payload.masterParent
        );
      })
      .addCase(getMasterParentProject.rejected, (state, action) => ({
        ...state,
        loading: false,
        error: action.error,
      }))

      .addCase(loadResourcesWithEmail.pending, (state) => ({
        ...state,
        loading: true,
      }))
      .addCase(loadResourcesWithEmail.fulfilled, (state, action) => {
        state.loading = false;
        resourcesAdapter.setMany(state.resources, action.payload.resources);
      })
      .addCase(loadResourcesWithEmail.rejected, (state, action) => ({
        ...state,
        loading: false,
        error: action.error,
      }))

      .addCase(loadResourcesByUser.pending, (state) => ({
        ...state,
        loading: true,
      }))
      .addCase(loadResourcesByUser.fulfilled, (state, action) => {
        state.loading = false;
        resourcesAdapter.setMany(state.resources, action.payload.resources);
      })
      .addCase(loadResourcesByUser.rejected, (state, action) => ({
        ...state,
        loading: false,
        error: action.error,
      }))
      .addCase(downloadFormsByType.pending, (state) => ({
        ...state,
        downloadingFormsByType: true,
      }))
      .addCase(downloadFormsByType.fulfilled, (state, action) => {
        state.downloadingFormsByType = false;
        state.downloadUrl = action.payload.response.downloadUrl;
      })
      .addCase(downloadFormsByType.rejected, (state, action) => ({
        ...state,
        downloadingFormsByType: false,
        error: action.error,
      }))

      .addCase(createForm.pending, (state) => ({
        ...state,
        createFormLoading: true,
      }))
      .addCase(createForm.fulfilled, (state, action) => {
        state.createFormLoading = false;
        const createdForm = action.payload.createdForm;
        formsAdapter.upsertOne(state.forms, createdForm);
        if (state.formState && Object.keys(state.formState).length > 0) {
          state.formState.id = createdForm.id;
          state.formState.mirrorUser = createdForm.mirrorUser;
        }
        // Keep the lazy policy's folder-header counts truthful without a
        // full /forms/summary re-fetch. Only while a summary is actually
        // loaded — nothing to correct if no one has fetched one yet.
        if (state.formsSummary.status === 'loaded' && createdForm) {
          const group = state.formsSummary.groups.find((existingGroup) =>
            formMatchesSummaryGroup(existingGroup, createdForm)
          );
          if (group) {
            group.count = (group.count || 0) + 1;
            if (
              createdForm.date &&
              (!group.latestDate || createdForm.date > group.latestDate)
            ) {
              group.latestDate = createdForm.date;
              // Same flag the server computes: scheduled and composed forms
              // carry UTC dates (see formatNewestFormLabel).
              group.latestDateScheduled = Boolean(
                createdForm.scheduled || createdForm.composed
              );
            }
            if (
              createdForm.createdAt &&
              (!group.latestCreatedAt ||
                createdForm.createdAt > group.latestCreatedAt)
            ) {
              group.latestCreatedAt = createdForm.createdAt;
            }
          } else {
            state.formsSummary.groups.push({
              type: createdForm.type,
              originalType: createdForm.originalType || null,
              template_group_id: createdForm.template_group_id || null,
              template_id: createdForm.template_id || null,
              count: 1,
              latestDate: createdForm.date || null,
              latestDateScheduled: createdForm.date
                ? Boolean(createdForm.scheduled || createdForm.composed)
                : null,
              latestCreatedAt: createdForm.createdAt || null,
            });
          }
        }
      })
      .addCase(createForm.rejected, (state, action) => ({
        ...state,
        createFormLoading: false,
        createFormError: action.error,
      }))

      .addCase(deleteForm.pending, (state) => ({
        ...state,
        deleteFormLoading: true,
      }))
      .addCase(deleteForm.fulfilled, (state, action) => {
        state.deleteFormLoading = false;
        const deletedFormId = action.payload.deletedForm.id;
        // Read before removeOne: the summary group a form belonged to can
        // only be found from the full entity, and it is gone right after.
        const deletedFormEntity = state.forms.entities[deletedFormId];
        formsAdapter.removeOne(state.forms, deletedFormId);
        if (state.formsSummary.status === 'loaded' && deletedFormEntity) {
          const group = state.formsSummary.groups.find((existingGroup) =>
            formMatchesSummaryGroup(existingGroup, deletedFormEntity)
          );
          if (group) {
            group.count = Math.max(0, (group.count || 0) - 1);
          }
        }
      })
      .addCase(deleteForm.rejected, (state, action) => ({
        ...state,
        deleteFormLoading: false,
        deleteFormError: action.error,
      }))

      .addCase(editForm.pending, (state) => ({
        ...state,
        editFormLoading: true,
      }))
      .addCase(editForm.fulfilled, (state, action) => {
        state.editFormLoading = false;
        formsAdapter.upsertOne(state.forms, action.payload.editedForm);
      })
      .addCase(editForm.rejected, (state, action) => ({
        ...state,
        editFormLoading: false,
        editFormError: action.error,
      }))

      .addCase(createOrientationFormTemplate.pending, (state) => ({
        ...state,
        createFormTemplateLoading: true,
      }))
      .addCase(createOrientationFormTemplate.fulfilled, (state, action) => {
        state.createFormTemplateLoading = false;
        formTemplatesAdapter.upsertOne(
          state.formTemplates,
          action.payload.createdFormTemplate
        );
      })
      .addCase(createOrientationFormTemplate.rejected, (state, action) => ({
        ...state,
        createFormTemplateLoading: false,
        createFormTemplateError: action.error,
      }))

      .addCase(editOrientationFormTemplate.pending, (state) => ({
        ...state,
        editFormTemplateLoading: true,
      }))
      .addCase(editOrientationFormTemplate.fulfilled, (state, action) => {
        state.editFormTemplateLoading = false;
        formTemplatesAdapter.upsertOne(
          state.formTemplates,
          action.payload.editedFormTemplate
        );
      })
      .addCase(editOrientationFormTemplate.rejected, (state, action) => ({
        ...state,
        editFormTemplateLoading: false,
        editFormTemplateError: action.error,
      }))

      .addCase(createFormTemplate.pending, (state) => ({
        ...state,
        createFormTemplateLoading: true,
      }))
      .addCase(createFormTemplate.fulfilled, (state, action) => {
        state.createFormTemplateLoading = false;
        formTemplatesAdapter.upsertOne(
          state.formTemplates,
          action.payload.createdFormTemplate
        );
      })
      .addCase(createFormTemplate.rejected, (state, action) => ({
        ...state,
        createFormTemplateLoading: false,
        createFormTemplateError: action.error,
      }))

      .addCase(editFormTemplate.pending, (state) => ({
        ...state,
        editFormTemplateLoading: true,
      }))
      .addCase(editFormTemplate.fulfilled, (state, action) => {
        state.editFormTemplateLoading = false;
        formTemplatesAdapter.upsertOne(
          state.formTemplates,
          action.payload.editedFormTemplate
        );
      })
      .addCase(editFormTemplate.rejected, (state, action) => ({
        ...state,
        editFormTemplateLoading: false,
        editFormTemplateError: action.error,
      }))

      .addCase(loadFormTemplatesForProject.pending, (state) => ({
        ...state,
        loadFormTemplatesByBusinessIdLoading: true,
      }))
      .addCase(loadFormTemplatesForProject.fulfilled, (state, action) => {
        state.loadFormTemplatesByBusinessIdLoading = false;
        formTemplatesAdapter.setMany(
          state.formTemplates,
          action.payload.formTemplates
        );
      })
      .addCase(loadFormTemplatesForProject.rejected, (state, action) => ({
        ...state,
        loadFormTemplatesByBusinessIdLoading: false,
        loadFormTemplatesByBusinessIdError: action.error,
      }))

      .addCase(loadDefaultFormTemplates.pending, (state) => ({
        ...state,
        loadFormTemplatesByBusinessIdLoading: true,
      }))
      .addCase(loadDefaultFormTemplates.fulfilled, (state, action) => {
        state.loadFormTemplatesByBusinessIdLoading = false;
        defaultTemplatesAdapter.setMany(
          state.defaultTemplates,
          action.payload.defaultTemplates
        );
      })
      .addCase(loadDefaultFormTemplates.rejected, (state, action) => ({
        ...state,
        loadFormTemplatesByBusinessIdLoading: false,
        loadDefaultFormTemplatesError: action.error,
      }))
      .addCase(loadProjectFormVisibilityControl.pending, (state) => ({
        ...state,
        loadingProjectFormVisibility: true,
      }))
      .addCase(loadProjectFormVisibilityControl.fulfilled, (state, action) => {
        state.loadingProjectFormVisibility = false;
        state.projectFormVisibility = action.payload.projectFormVisibility;
        state.projectFormVisibilityHydrated = true;
      })
      .addCase(loadProjectFormVisibilityControl.rejected, (state, action) => ({
        ...state,
        loadingProjectFormVisibility: false,
        loadProjectFormVisibilityError: action.error,
      }))
      .addCase(saveProjectFormVisibilityControl.pending, (state) => ({
        ...state,
        savingProjectFormVisibility: true,
      }))
      .addCase(saveProjectFormVisibilityControl.fulfilled, (state, action) => {
        state.savingProjectFormVisibility = false;
        state.projectFormVisibility = action.payload.projectFormVisibility;
        state.projectFormVisibilityHydrated = true;
      })
      .addCase(saveProjectFormVisibilityControl.rejected, (state, action) => ({
        ...state,
        savingProjectFormVisibility: false,
        saveProjectFormVisibilityError: action.error,
      }))

      .addCase(setNotes.pending, (state) => ({
        ...state,
        createNotesLoading: true,
      }))
      .addCase(setNotes.fulfilled, (state, action) => {
        state.createNotesLoading = false;
        formNotesAdapter.setMany(state.formNotes, action.payload.notesSet);
      })
      .addCase(setNotes.rejected, (state, action) => ({
        ...state,
        createNotesLoading: false,
        createNotesError: action.error,
      }))

      .addCase(getNotes.pending, (state) => ({
        ...state,
        getNotesLoading: true,
      }))
      .addCase(getNotes.fulfilled, (state, action) => {
        state.getNotesLoading = false;
        formNotesAdapter.setMany(state.formNotes, action.payload.formNotes);
      })
      .addCase(getNotes.rejected, (state, action) => ({
        ...state,
        getNotesLoading: false,
        getNotesError: action.error,
      }))

      .addCase(sendForm.pending, (state) => ({
        ...state,
        sendFormLoading: true,
      }))
      .addCase(sendForm.fulfilled, (state) => {
        state.sendFormLoading = false;
      })
      .addCase(sendForm.rejected, (state, action) => ({
        ...state,
        sendFormLoading: false,
        sendFormError: action.error,
      }))

      .addCase(getParentSubProjects.pending, (state) => ({
        ...state,
        loading: true,
      }))
      .addCase(getParentSubProjects.fulfilled, (state, action) => {
        state.loading = false;
        subProjectsAdapter.setMany(
          state.subProjects,
          action.payload.subProjects
        );
      })
      .addCase(getParentSubProjects.rejected, (state, action) => ({
        ...state,
        loading: false,
        error: action.error,
      }))

      .addCase(createManualResource.pending, (state) => ({
        ...state,
        createManualResourceLoading: true,
      }))
      .addCase(createManualResource.fulfilled, (state, action) => {
        state.createManualResourceLoading = false;
        manualResourcesAdapter.addOne(
          state.manualResources,
          action.payload.createdManualResource
        );
      })
      .addCase(createManualResource.rejected, (state, action) => ({
        ...state,
        createManualResourceLoading: false,
        createManualResourceError: action.error,
      }))

      .addCase(loadFormSendData.pending, (state) => ({
        ...state,
        forms: {
          ...state.forms,
          loadingFormsSendData: true,
        },
      }))
      .addCase(loadFormSendData.fulfilled, (state, action) => {
        state.forms.loadingFormsSendData = false;
        // resourcesAdapter.setMany(state.resources, action.payload.resources);
        manualResourcesAdapter.setMany(
          state.manualResources,
          action.payload.manualResources
        );
      })
      .addCase(loadFormSendData.rejected, (state, action) => ({
        ...state,
        loadingFormsSendData: false,
        error: action.error,
      }))
      .addCase(loadRecordsForResources.pending, (state) => ({
        ...state,
        loading: true,
      }))
      .addCase(loadRecordsForResources.fulfilled, (state, action) => {
        state.loading = false;
        state.resources = action.payload.resources;
      })
      .addCase(loadRecordsForResources.rejected, (state, action) => ({
        ...state,
        loading: false,
        error: action.error,
      }))
      .addCase(projectFormActions.resetDownloadData, (state, action) => ({
        ...state,
        downloadUrl: null,
        loadingFormsByType: false,
      }))
      .addCase(projectFormActions.editFormLocally, (state, action) => {
        if (action.payload?.id) {
          formsAdapter.upsertOne(state.forms, action.payload);
        }
      })
      .addCase(projectFormActions.resetFormTypeStatuses, (state) => ({
        ...state,
        formTypeStatus: {},
        formTypeRequestIds: {},
        formsSummary: defaultFormsSummaryState,
      }))
      .addCase(projectFormActions.resetResources, (state, action) => ({
        ...state,
        resources: [],
      }))
      .addCase(projectFormActions.resetProjectForms, (state, action) => ({
        ...state,
        ...resetState,
      }))
      .addCase(projectFormActions.setFormState, (state, action) => ({
        ...state,
        formState: {
          ...state.formState,
          ...action.payload,
        },
      }))
      .addCase(setFormStateAsync.fulfilled, (state, action) => ({
        ...state,
        formStateAsyncLoading: false,
        ...action.payload,
      }))
      .addCase(projectFormActions.removeFormState, (state, action) => ({
        ...state,
        formState: {},
        unsavedChanges: undefined,
      }))
      .addCase(projectFormActions.removeUserOrientationData, (state, action) => ({
        ...state,
        userOrientationData: {},
        loadingUserOrientationData: false,
      }))
      .addCase(projectFormActions.setSnackBarState, (state, action) => ({
        ...state,
        snackBarState: {
          ...state.snackBarState,
          ...action.payload,
        },
      }))
      .addCase(projectFormActions.setLoading, (state, action) => ({
        ...state,
        loading: action.payload,
      }))
      .addCase(
        projectFormActions.setFormNotesFilePermissions,
        (state, action) => ({
          ...state,
          formNotesFilePermissions: { ...action.payload },
        })
      )
      .addCase(projectFormActions.setCompanyLogoState, (state, action) => ({
        ...state,
        companyLogo: action.payload,
      }))
      .addCase(projectFormActions.setFormLabels, (state, action) => ({
        ...state,
        formLabels: action.payload,
      }))
      .addCase(projectFormActions.setOpenedItemsList, (state, action) => ({
        ...state,
        openedItemsList: action.payload,
      }))
      .addCase(
        projectFormActions.setResourcesGroupedByEmployer,
        (state, action) => ({
          ...state,
          resourcesGroupedByEmployer: action.payload,
        })
      )
      .addCase(projectFormActions.setCurrentFormState, (state, action) => ({
        ...state,
        currentFormState: {
          ...state.currentFormState,
          ...action.payload,
        },
      }))
      .addCase(projectFormActions.setUnsavedChanges, (state, action) => ({
        ...state,
        unsavedChanges: action.payload,
      }))
      .addCase(projectFormActions.setHasChanges, (state, action) => ({
        ...state,
        hasChanges: action.payload,
      }))
      .addCase(projectFormActions.setFormNotesForSave, (state, action) => ({
        ...state,
        notesForSave: action.payload,
      }))
      .addCase(
        projectFormActions.setInitialTRFormNotesAsSet,
        (state, action) => ({
          ...state,
          initialTRFormNotesSet: action.payload,
        })
      )
      .addCase(projectFormActions.setWorkers, (state, action) => ({
        ...state,
        workers: action.payload,
      }))
      .addCase(projectFormActions.resetWorkers, (state, action) => ({
        ...state,
        workers: defaultWorkersState,
      }))
      .addCase(projectFormActions.setFormTemplateChanges, (state, action) => ({
        ...state,
        formTemplateChanges: {
          ...state.formTemplateChanges,
          ...action.payload,
        },
      }))
      .addCase(
        projectFormActions.resetFormTemplateChanges,
        (state, action) => ({
          ...state,
          formTemplateChanges: {},
        })
      )
      .addCase(projectFormActions.setImageLoadingProgress, (state, action) => ({
        ...state,
        imageLoadingProgress: {
          progress: action.payload.progress,
          formId: action.payload.formId,
        },
      }))
      .addCase(
        projectFormActions.setCurrentlyLoadingFormId,
        (state, action) => ({
          ...state,
          currentlyLoadingFormId: action.payload,
        })
      )
      .addCase(projectFormActions.setIsCancelled, (state, action) => ({
        ...state,
        isCancelled: action.payload,
      }))
      .addCase(projectFormActions.setIsFormSaving, (state, action) => ({
        ...state,
        isFormSaving: action.payload,
      }))
      .addCase(
        projectFormActions.setUploadingProgressValues,
        (state, action) => ({
          ...state,
          uploadingProgressValues: action.payload,
        })
      )
      .addCase(
        projectFormActions.setHasMissingOrientationForms,
        (state, action) => ({
          ...state,
          hasMissingOrientationForms: action.payload,
        })
      );
  },
});

export const {
  selectAll: selectAllForms,
  selectEntities: selectFormEntities,
  selectById: selectFormById,
  selectIds: selectFormIds,
} = formsAdapter.getSelectors((state) => state.projectForms.forms);

export const {
  selectAll: selectAllFormTemplates,
  selectEntities: selectFormTemplateEntities,
  selectById: selectFormTemplateById,
  selectIds: selectFormTemplateIds,
} = formTemplatesAdapter.getSelectors(
  (state) => state.projectForms.formTemplates
);

export const {
  selectAll: selectAllDefaultFormTemplates,
  selectEntities: selectDefaultFormTemplateEntities,
  selectById: selectDefaultFormTemplateById,
  selectIds: selectDefaultFormTemplateIds,
} = defaultTemplatesAdapter.getSelectors(
  (state) => state.projectForms.defaultTemplates
);

export default projectFormSlice.reducer;
